import React, { Fragment, useRef } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { Button, Div, Span } from 'components/Ui';
import Loader from 'components/Loader';
import { ValidatorForm } from 'react-material-ui-form-validator';
import * as drawerActions from 'containers/DrawerManager/actions';
import * as paymentActions from 'containers/PrimoProfile/actions';
import { BlackBtn } from 'images/index';
import PaymentRemovalSuccessDrawer from 'containers/PrimoProfile/BillingInformation/PaymentRemovalSuccessDrawer';
import { GTM_TRACKING_TAGS } from 'utils/constants';
import {
  selectBankAccounts,
  selectCreditCards,
  selectIsBankAccountFetching,
  selectIsCardsFetching,
  selectIsProcessing,
  selectIsStatementProcessing,
  selectIsPaymentRemovalSuccessful,
  selectOneTimeCreditCard,
} from '../selectors';
import PaymentMethodBlock from './PaymentMethodBlock';
import EditCreditCardDrawer from './CreditCardForm/EditCreditCardDrawer';
import EditBankAccountDrawer from './BankAccountForm/EditBankAccountDrawer';
import { EDIT_CREDIT_CARD, EDIT_BANK_ACCOUNT } from './constants';

import commonStyles from '../styles.scss';
import styles from './styles.scss';
const BillingInformationForm = ({
  onClose,
  creditCards,
  bankAccounts,
  showDrawer,
  resetAddCardSucessful,
  isProcessing,
  isCardsFetching,
  isBankAccountFetching,
  isStatementProcessing,
  isPaymentRemovalSuccessful,
  oneTimeCreditCard,
}) => {
  const formContainer = useRef(null);

  const additionalCreditCards = [...creditCards, oneTimeCreditCard].filter(
    (creditCard) => creditCard && !creditCard.autopay,
  );
  const additionalBankAccounts = bankAccounts.filter(
    (bankAccount) => !bankAccount.autopay,
  );
  const autoPayPaymentMethod = [...creditCards, ...bankAccounts].find(
    (acc) => acc.autopay,
  );

  const isShowCards = additionalCreditCards.length !== 0;
  const isShowAccounts = additionalBankAccounts.length !== 0;

  const showCreditCardForm = () => {
    resetAddCardSucessful();
    showDrawer(EDIT_CREDIT_CARD);
  };

  const showBankAccountForm = () => showDrawer(EDIT_BANK_ACCOUNT);

  return (
    <Fragment>
      <ValidatorForm className={commonStyles.form} ref={formContainer}>
        {autoPayPaymentMethod && (
          <div className={commonStyles.formContainer}>
            <Div className={commonStyles.header}>AutoPay payment method</Div>

            <PaymentMethodBlock
              type={autoPayPaymentMethod.cardType ? 'card' : 'account'}
              data={autoPayPaymentMethod}
              locked
            />
          </div>
        )}
      </ValidatorForm>

      <div className={styles.additionalPaymentsContainer}>
        <Div className={commonStyles.header}>Saved payment methods</Div>
        <div data-tracking={GTM_TRACKING_TAGS['Debit/Credit Cards']}>
          <Loader loading={isCardsFetching} local>
            {isShowCards && (
              <div className={styles.additionalPayments}>
                <Span>Your credit/debit cards</Span>
                {additionalCreditCards.map((creditCard) => (
                  <PaymentMethodBlock
                    key={creditCard.id}
                    type="card"
                    data={creditCard}
                    onClose={onClose}
                  />
                ))}
              </div>
            )}
          </Loader>
          <div className={classnames(styles.cards, styles.addPayments)}>
            <div className={styles.addPaymentsContainer}>
              <Button
                icon={
                  <img src={BlackBtn} alt="add" className={styles.addLabel} />
                }
                onClick={showCreditCardForm}
                link
                tracking="Add Payment Method"
                data-tracking="Add Card"
                type="button"
              />
              <Span>Add a new credit/debit card</Span>
            </div>
          </div>
        </div>
        <div data-tracking={GTM_TRACKING_TAGS['Bank Accounts']}>
          <Loader loading={isBankAccountFetching} local>
            {isShowAccounts && (
              <div
                className={classnames(
                  styles.additionalPayments,
                  styles.bankAccounts,
                )}
              >
                <Span>Your bank accounts</Span>
                {additionalBankAccounts.map((bankAccount) => (
                  <PaymentMethodBlock
                    key={bankAccount.id}
                    type="account"
                    data={bankAccount}
                    onClose={onClose}
                  />
                ))}
              </div>
            )}
          </Loader>
          <div className={classnames(styles.cards, styles.addPayments)}>
            <div className={styles.addPaymentsContainer}>
              <Button
                icon={
                  <img src={BlackBtn} alt="add" className={styles.addLabel} />
                }
                onClick={showBankAccountForm}
                link
                type="button"
                tracking="Add Payment Method"
                data-tracking="Add a new bank account"
                ariaLabel="Add a new bank account"
              />
              <Span>Add a new bank account</Span>
            </div>
          </div>
        </div>
      </div>

      <div className={commonStyles.bottom}>
        <div className={commonStyles.buttons}>
          <Button
            className={commonStyles.cancelButton}
            text="Done"
            onClick={onClose}
            disabled={isProcessing || isStatementProcessing}
          />
        </div>
      </div>
      <EditCreditCardDrawer
        drawerProps={{ onClose }}
        formProps={{ isNew: true }}
      />
      <EditBankAccountDrawer
        drawerProps={{ onClose }}
        formProps={{ isNew: true }}
      />
      <PaymentRemovalSuccessDrawer open={isPaymentRemovalSuccessful} />
    </Fragment>
  );
};

BillingInformationForm.propTypes = {
  onClose: PropTypes.func,
  oneTimeCreditCard: PropTypes.object,
  creditCards: PropTypes.array,
  bankAccounts: PropTypes.array,
  showDrawer: PropTypes.func.isRequired,
  isProcessing: PropTypes.bool.isRequired,
  isCardsFetching: PropTypes.bool.isRequired,
  isStatementProcessing: PropTypes.bool.isRequired,
  isBankAccountFetching: PropTypes.bool.isRequired,
  resetAddCardSucessful: PropTypes.func,
  isPaymentRemovalSuccessful: PropTypes.bool,
};

const mapStateToProps = createStructuredSelector({
  creditCards: selectCreditCards(),
  bankAccounts: selectBankAccounts(),
  isProcessing: selectIsProcessing(),
  isCardsFetching: selectIsCardsFetching(),
  isBankAccountFetching: selectIsBankAccountFetching(),
  isStatementProcessing: selectIsStatementProcessing(),
  isPaymentRemovalSuccessful: selectIsPaymentRemovalSuccessful(),
  oneTimeCreditCard: selectOneTimeCreditCard(),
});

const mapDispatchToProps = {
  showDrawer: drawerActions.showDrawer,
  resetAddCardSucessful: paymentActions.resetAddCardSucessful,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(BillingInformationForm);
